import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Trophy, Users, Zap, Swords, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getTournaments, registerTeamForTournament, getTeam, formatApiError } from "@/lib/api";
import BracketDialog from "@/components/BracketDialog";
import { useAuth } from "@/lib/auth";

const STATUS_STYLES = {
  upcoming: { label: "Upcoming", cls: "text-[#F3E5AB] border-[#D4AF37]/40 bg-[#D4AF37]/10" },
  live: { label: "Live Now", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10 animate-pulse" },
  ended: { label: "Concluded", cls: "text-zinc-400 border-zinc-700 bg-zinc-900/50" },
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function EnlistButton({ tournament, onRegistered }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open && user?.team_id) {
      getTeam(user.team_id).then(setTeam).catch(() => setTeam(null));
    }
  }, [open, user?.team_id]);

  const isCaptain = team && user && team.captain_id === user.user_id;
  const tournamentFull = tournament.registered >= tournament.slots;
  const ended = tournament.status === "ended";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerTeamForTournament(tournament.id, { notes });
      toast.success("Squad locked in.");
      setOpen(false);
      setNotes("");
      onRegistered();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  if (ended) {
    return (
      <button disabled data-testid={`tournament-enlist-${tournament.id}`}
        className="w-full bg-zinc-800 text-zinc-500 font-bold uppercase tracking-widest text-xs px-5 py-3 cut-corners cursor-not-allowed">
        Concluded
      </button>
    );
  }

  if (!user) {
    return (
      <Link to="/auth"
        data-testid={`tournament-enlist-${tournament.id}`}
        className="block text-center btn-shimmer bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 cut-corners transition-colors">
        Login to Enlist
      </Link>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`tournament-enlist-${tournament.id}`}
          disabled={tournamentFull}
          className="w-full btn-shimmer bg-[#D4AF37] hover:bg-[#B39028] disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-black font-bold uppercase tracking-widest text-xs px-5 py-3 cut-corners transition-colors">
          {tournamentFull ? "Bracket Full" : "Enlist Squad"}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="tournament-signup-dialog"
        className="bg-[#0A0A0A] border border-[#D4AF37]/30 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Enlist Squad</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Register your team in {tournament.name}.
          </DialogDescription>
        </DialogHeader>

        {!user.team_id && (
          <div className="border border-yellow-500/30 bg-yellow-500/5 cut-corners p-4 text-sm text-yellow-200/90 flex items-start gap-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>
              You need to be on a team first.{" "}
              <Link to="/teams" className="underline text-[#D4AF37]" onClick={() => setOpen(false)}>
                Create or join a team
              </Link>{" "}
              to enlist.
            </div>
          </div>
        )}

        {user.team_id && team && !isCaptain && (
          <div className="border border-yellow-500/30 bg-yellow-500/5 cut-corners p-4 text-sm text-yellow-200/90 flex items-start gap-3">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <div>
              Only your team captain can enlist <strong>{team.name}</strong> for tournaments.
            </div>
          </div>
        )}

        {user.team_id && isCaptain && (
          <form onSubmit={submit} className="grid gap-4">
            <div className="border border-[#D4AF37]/20 bg-black/40 cut-corners p-3 text-sm text-zinc-300">
              Registering: <span className="font-display uppercase text-white">[{team.tag}] {team.name}</span> · {team.members?.length || 0} members
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Notes (optional)</Label>
              <Textarea data-testid="signup-notes-input"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={3} placeholder="Region, scheduling preferences, etc."
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="signup-submit-button"
                className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
                {submitting ? "Locking in..." : "Lock In Squad"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Tournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bracketTournament, setBracketTournament] = useState(null);

  const reload = () => {
    setLoading(true);
    getTournaments()
      .then(setTournaments)
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  return (
    <section id="tournaments" data-testid="tournaments-section"
      className="relative py-24 lg:py-32 bg-[#050505] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.08)_0%,transparent_60%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">⌁ The Gauntlet</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
              Tournaments &amp; <span className="gold-text">Crowns</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="tgl-card cut-corners h-64 animate-pulse" />
              ))
            : tournaments.map((t, i) => {
                const status = STATUS_STYLES[t.status] || STATUS_STYLES.upcoming;
                const fillPct = t.slots ? Math.min(100, Math.round((t.registered / t.slots) * 100)) : 0;
                return (
                  <motion.div key={t.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: i * 0.06 }}
                    className="tgl-card cut-corners p-6 lg:p-8 flex flex-col gap-5"
                    data-testid={`tournament-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.25em] border px-2 py-1 ${status.cls}`}>
                          {status.label}
                        </span>
                        <h3 className="mt-3 font-display text-2xl lg:text-3xl font-black uppercase text-white">{t.name}</h3>
                        <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{t.game}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Prize</div>
                        <div className="font-display text-2xl font-black gold-text">{t.prize_pool}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Calendar size={14} className="text-[#D4AF37]" />
                        <span>{t.starts_label || formatDate(t.starts_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Zap size={14} className="text-[#D4AF37]" />
                        <span className="truncate">{t.format}</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Users size={14} className="text-[#D4AF37]" />
                        <span>{t.registered}/{t.slots}</span>
                      </div>
                    </div>

                    <div>
                      <div className="h-1 bg-white/5 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#8E6A1F] via-[#D4AF37] to-[#FFE9A0]"
                          style={{ width: `${fillPct}%` }} />
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500">
                        Bracket {fillPct}% filled
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <Trophy size={16} className="text-[#D4AF37]" />
                      <div className="flex-1 min-w-[140px]">
                        <EnlistButton tournament={t} onRegistered={reload} />
                      </div>
                      {t.has_bracket && (
                        <button onClick={() => setBracketTournament(t)}
                          data-testid={`tournament-view-bracket-${t.id}`}
                          className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-widest text-xs px-4 py-3 cut-corners transition-colors">
                          <Swords size={14} /> View Bracket
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
        </div>
      </div>

      <BracketDialog tournament={bracketTournament} open={!!bracketTournament}
        onOpenChange={(v) => !v && setBracketTournament(null)} />
    </section>
  );
}
